import { useEffect, Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Select as AntdSelect, Space } from 'antd';
import { PolicyInfoResponse, useProfileImage } from 'all-in-one-shared-api';
import { SelectionName, SelectionPeriod } from 'pages/benefits/types/benefitTypes';
import useGetBenefitFilters from 'pages/benefits/hooks/useGetBenefitFilters';
import { Select } from 'components/select';
import { useKeycloakReady } from 'hooks/useKeycloakReady';
import { DEFAULT_DATE_FORMAT, formatDateString } from 'utils/datetime';
import { Avatar } from 'components/avatar';

const filterStyles = {
  width: '210px',
};

const formatDate = (dateString: string): string => formatDateString(dateString, 'yyyy-MM', DEFAULT_DATE_FORMAT);

export interface BenefitFiltersProp {
  policyInfoData?: Array<PolicyInfoResponse>;
  selectedFilterName: Partial<SelectionName>;
  selectedFilterPeriod: Partial<SelectionPeriod>;
  setSelectedFilterName: Dispatch<SetStateAction<Partial<SelectionName>>>;
  setSelectedFilterPeriod: Dispatch<SetStateAction<Partial<SelectionPeriod>>>;
}

function BenefitFilters({
  policyInfoData,
  selectedFilterName,
  selectedFilterPeriod,
  setSelectedFilterName,
  setSelectedFilterPeriod,
}: BenefitFiltersProp) {
  const { token } = useKeycloakReady();
  const { t } = useTranslation();

  const memberTypes = policyInfoData?.map((policyInfo) => policyInfo.memberType).join(',');

  const {
    shouldFetch: fetchProfileImage,
    data: profileImageData,
    error: profileImageError,
  } = useProfileImage({ name: 'getProfileImage', memberTypes }, token ?? '', memberTypes);

  const { selectedFilterNameOptions, onFilterNameSelected, selectedFilterPeriodOptions, onFilterPeriodSelected } =
    useGetBenefitFilters({
      policyInfos: policyInfoData || [],
      selectedFilterName,
      selectedFilterPeriod,
      setSelectedFilterName,
      setSelectedFilterPeriod,
    });

  useEffect(() => {
    fetchProfileImage(!!memberTypes);
  }, [memberTypes, fetchProfileImage]);

  const profileImageMap = new Map<string, string | undefined>();
  if (profileImageData && !profileImageError) {
    profileImageData?.forEach((data) => {
      if (data.memberType) {
        profileImageMap.set(data.memberType, data.pictureUrl);
      }
    });
  }

  const translatedSelectedFilterPeriodOptions = selectedFilterPeriodOptions.map((opt) => {
    return {
      ...opt,
      label: `${formatDate(opt.startDate ?? '')} ${t('common.to')} ${formatDate(opt.endDate ?? '')}`,
    };
  });

  return (
    <Space size="middle" style={{ marginTop: '16px' }}>
      <AntdSelect value={selectedFilterName?.id || ''} onChange={onFilterNameSelected} style={filterStyles}>
        {selectedFilterNameOptions.map((option) => {
          const avatarUrl = profileImageMap?.get(option.memberType ?? '');
          return (
            <AntdSelect.Option key={option.id}>
              <Space>
                <Avatar size="small" avatarUrl={avatarUrl} />
                {`${option.firstName} ${option.lastName}`}
              </Space>
            </AntdSelect.Option>
          );
        })}
      </AntdSelect>
      <Select
        options={translatedSelectedFilterPeriodOptions}
        value={selectedFilterPeriod?.id || ''}
        onChange={onFilterPeriodSelected}
        style={filterStyles}
      />
    </Space>
  );
}

export default BenefitFilters;
